// frontend/src/utils/api.js
// API client for backend communication

const API_BASE = '/api';

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
}

// Auth APIs
export async function register(email, password, displayName) {
  const res = await fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, displayName })
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Registration failed');
  }
  return res.json();
}

export async function login(email, password) {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Login failed');
  }
  return res.json();
}

export async function logout() {
  const res = await fetch(`${API_BASE}/auth/logout`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Logout failed');
  }
  return res.json();
}

export async function getCurrentUser() {
  const res = await fetch(`${API_BASE}/auth/me`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) {
    throw new Error('Failed to get user');
  }
  return res.json();
}

export async function updateProfile({ displayName }) {
  const res = await fetch(`${API_BASE}/auth/profile`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ displayName })
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({ error: 'Update failed' }));
    throw new Error(e.error || 'Update failed');
  }
  return res.json();
}

export async function changePassword({ currentPassword, newPassword }) {
  const res = await fetch(`${API_BASE}/auth/change-password`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ currentPassword, newPassword })
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({ error: 'Password change failed' }));
    throw new Error(e.error || 'Password change failed');
  }
  return res.json();
}

export async function deleteAccount({ password }) {
  const res = await fetch(`${API_BASE}/auth/me`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    body: JSON.stringify({ password })
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({ error: 'Delete failed' }));
    throw new Error(e.error || 'Delete failed');
  }
  return res.json();
}

// Patent APIs
export async function getAllProjects() {
  const res = await fetch(`${API_BASE}/patents`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) {
    throw new Error('Failed to fetch projects');
  }
  return res.json();
}

export async function createProject(inventionTitle, inventionDescription, mindblowerContext) {
  const res = await fetch(`${API_BASE}/patents`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ inventionTitle, inventionDescription, mindblowerContext })
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to create project');
  }
  return res.json();
}

export async function getProject(projectId) {
  const res = await fetch(`${API_BASE}/patents/${projectId}`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) {
    throw new Error('Failed to fetch project');
  }
  return res.json();
}

export async function getProjectMessages(projectId) {
  const res = await fetch(`${API_BASE}/patents/${projectId}/messages`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) {
    throw new Error('Failed to fetch messages');
  }
  return res.json();
}

export async function getProjectIssues(projectId) {
  const res = await fetch(`${API_BASE}/patents/${projectId}/issues`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error('Failed to fetch issues');
  return res.json();
}

export function exportProjectUrl(projectId, format) {
  const token = localStorage.getItem('token');
  return `${API_BASE}/patents/${projectId}/export?format=${format}&token=${encodeURIComponent(token)}`;
}

export async function downloadProjectExport(projectId, format) {
  const res = await fetch(`${API_BASE}/patents/${projectId}/export?format=${format}`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({ error: 'Export failed' }));
    throw new Error(e.error || 'Export failed');
  }
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match ? match[1] : `patent-council.${format}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function getProjectChecklist(projectId) {
  const res = await fetch(`${API_BASE}/patents/${projectId}/checklist`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error('Failed to fetch checklist');
  return res.json();
}

export async function getProjectDraft(projectId) {
  const res = await fetch(`${API_BASE}/patents/${projectId}/draft`, {
    headers: getAuthHeaders()
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to fetch draft');
  return res.json();
}

export async function getProjectDrafts(projectId) {
  const res = await fetch(`${API_BASE}/patents/${projectId}/drafts`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error('Failed to fetch draft history');
  return res.json();
}

export async function updateProject(projectId, updates) {
  const res = await fetch(`${API_BASE}/patents/${projectId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates)
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to update project');
  }
  return res.json();
}

export async function deleteProject(projectId) {
  const res = await fetch(`${API_BASE}/patents/${projectId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to delete project');
  }
  return res.json();
}

// Seed APIs
export async function parseSeedUpload(fileOrText) {
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  let res;
  if (fileOrText instanceof File) {
    const fd = new FormData();
    fd.append('file', fileOrText);
    res = await fetch(`${API_BASE}/patents/seed/parse`, {
      method: 'POST',
      headers,
      body: fd
    });
  } else {
    res = await fetch(`${API_BASE}/patents/seed/parse`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: String(fileOrText || '') })
    });
  }
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Parse failed' }));
    throw new Error(error.error || 'Failed to parse seed');
  }
  return res.json();
}

export async function createSeededProject(payload) {
  const res = await fetch(`${API_BASE}/patents/seed`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Create failed' }));
    throw new Error(error.error || 'Failed to create seeded project');
  }
  return res.json();
}

// HIL controls
export async function extendRoundCap(projectId, { add, set }) {
  const res = await fetch(`${API_BASE}/patents/${projectId}/cap`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(add != null ? { add } : { set })
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({ error: 'Failed to extend cap' }));
    throw new Error(e.error || 'Failed to extend cap');
  }
  return res.json();
}

export async function requestInterrupt(projectId) {
  const res = await fetch(`${API_BASE}/patents/${projectId}/interrupt`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({ error: 'Failed to request interrupt' }));
    throw new Error(e.error || 'Failed to request interrupt');
  }
  return res.json();
}

export async function postRoundComment(projectId, { comment, visibility, round }) {
  const res = await fetch(`${API_BASE}/patents/${projectId}/round-comment`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ comment, visibility, round })
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({ error: 'Failed to post comment' }));
    throw new Error(e.error || 'Failed to post comment');
  }
  return res.json();
}

// Session API (SSE) — each connection runs ONE round.
// Events: roundStart, roundResume, agentMessage (many),
//         viabilityCheck, roundComplete | converged | capReached | notViable | paused | error
export function openSessionStream(projectId, callbacks) {
  const token = localStorage.getItem('token');
  const url = `${API_BASE}/sessions/${projectId}/stream?token=${encodeURIComponent(token)}`;

  const eventSource = new EventSource(url, { withCredentials: true });

  const wire = (name, cb, closeAfter = false) => {
    eventSource.addEventListener(name, (event) => {
      const data = event.data ? JSON.parse(event.data) : {};
      if (cb) cb(data);
      if (closeAfter) eventSource.close();
    });
  };

  wire('roundStart', callbacks.onRoundStart);
  wire('roundResume', callbacks.onRoundResume);
  wire('agentMessage', callbacks.onAgentMessage);
  wire('viabilityCheck', callbacks.onViabilityCheck);
  wire('normalizing', callbacks.onNormalizing);
  wire('normalizationComplete', callbacks.onNormalizationComplete);
  wire('normalizationFailed', callbacks.onNormalizationFailed);
  wire('issueAdded', callbacks.onIssueAdded);
  wire('rateLimitWaiting', callbacks.onRateLimitWaiting);
  wire('conceptBriefBuilding', callbacks.onConceptBriefBuilding);
  wire('conceptBriefComplete', callbacks.onConceptBriefComplete);
  wire('conceptBriefFailed', callbacks.onConceptBriefFailed, true);
  wire('researching', callbacks.onResearching);
  wire('researchComplete', callbacks.onResearchComplete);
  wire('researchFailed', callbacks.onResearchFailed, true);
  wire('initialDraftBuilding', callbacks.onInitialDraftBuilding);
  wire('diagramsBuilding', callbacks.onDiagramsBuilding);
  wire('diagramsReady', callbacks.onDiagramsReady);
  wire('diagramsFailed', callbacks.onDiagramsFailed);
  wire('initialDraftReady', callbacks.onInitialDraftReady, true);
  wire('initialDraftFailed', callbacks.onInitialDraftFailed, true);
  wire('draftRevising', callbacks.onDraftRevising);
  wire('draftRevised', callbacks.onDraftRevised);
  wire('draftRevisionFailed', callbacks.onDraftRevisionFailed);
  wire('triageStart', callbacks.onTriageStart);
  wire('triageComplete', callbacks.onTriageComplete);
  wire('discussionStart', callbacks.onDiscussionStart);
  wire('discussionFraming', callbacks.onDiscussionFraming);
  wire('discussionProposal', callbacks.onDiscussionProposal);
  wire('discussionSynthesis', callbacks.onDiscussionSynthesis);
  wire('discussionsComplete', callbacks.onDiscussionsComplete);
  wire('discussionSkipped', callbacks.onDiscussionSkipped);
  wire('roundComplete', callbacks.onRoundComplete, true);
  wire('paused', callbacks.onPaused, true);
  wire('capReached', callbacks.onCapReached, true);
  wire('notViable', callbacks.onNotViable, true);
  wire('converged', callbacks.onConverged, true);
  wire('synthesis', callbacks.onSynthesis);
  wire('checklist', callbacks.onChecklist);
  wire('designAround', callbacks.onDesignAround);

  eventSource.addEventListener('error', (event) => {
    const data = event.data ? (() => { try { return JSON.parse(event.data); } catch { return {}; } })() : {};
    if (callbacks.onError) callbacks.onError({ ...data, readyState: eventSource.readyState });
    eventSource.close();
  });

  return eventSource;
}
